/**
 * Black Friday 2026 campaign: the band at the top of every screen, and the one-time modal under it.
 *
 * The code is `blackfriday2026` and it grants 48 hours in which nothing is charged — no credits, no
 * feature units, no daily cap. Redeeming is the SAME call the `/promo/<code>` campaign link and the
 * Settings "Redeem a code" form already make (`redeemPromo`), so there is one redemption rule rather
 * than three. The window's end arrives as `freeUsageUntil` on GetCreditsOverview.
 *
 * Everything here is pure, so the five states are testable without React — and so web, iOS and Android
 * can be read side by side and checked case for case (`blackFridayBannerState` ·
 * `BlackFriday.bannerState` · `blackFridayBannerState`).
 *
 * ⚠️ WHY THE "OVER" BANNERS NEED A REMEMBERED END TIME. `CreditsOverview.java` reports `freeUsageUntil`
 * only while it is still in the future (`.filter(until -> until > now)`), and the balance API has no
 * "did this account ever redeem" read. So once the window closes the server tells a client NOTHING
 * about it ever having existed. The only way to show "your 48 free hours are over" is for the client to
 * remember the end it saw while the window was open. That makes the two over-banners PER DEVICE, like
 * the dismissals beside them: someone who redeemed on the web and never opened the Android app during
 * the window gets no over-banner there. That is a gap in what the server exposes, not a bug here —
 * closing it means a new field or RPC, which this change deliberately does not add.
 */

/** The campaign's code. One literal, read by the banner, the modal and the parity test. */
export const BLACK_FRIDAY_PROMO_CODE = 'blackfriday2026';

/** `?source=` on the redemption, so the funnel can tell the band from the modal from the link. */
export const BLACK_FRIDAY_BANNER_SOURCE = 'black_friday_banner';
export const BLACK_FRIDAY_MODAL_SOURCE = 'black_friday_modal';

/**
 * When the countdown band switches from "Free unlimited credits" to "Final hours of unlimited credits".
 *
 * Eight hours — the written brief's own number
 * (`artifacts/bfcm-packs/bfcm-in-product-assets-dev-brief.md`), confirmed on 2026-10-17. It shipped as
 * six for a while on a later verbal call; the two agree again. One constant, shared by the three
 * surfaces, so the copy cannot switch at a different hour on one of them.
 */
export const BLACK_FRIDAY_FINAL_HOURS_MS = 8 * 60 * 60 * 1000;

/**
 * The band's height before it has been measured — the design's one-line desktop bar (56) plus the slot's
 * own 24 top padding. Only the first frame reads it: `AppFrame` MEASURES the real height, because the two
 * over-bands wrap at narrow widths and in a long translation.
 */
export const BLACK_FRIDAY_BANNER_HEIGHT_PX = 80;

/** Retries after a failed wins read, before the band settles on "See plans". Same on all three surfaces. */
export const BLACK_FRIDAY_WINS_RETRIES = 2;

/** What the code grants. Used only to date the window's START from its end, for the wins lookback. */
export const BLACK_FRIDAY_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * The bands of the design (Figma `4217:25450`), plus `hidden`.
 *
 * The two `ended-*` bands are ONE band with two destinations, and which one a reader gets is decided
 * by the wins and by whether the reader already opened them, for every plan:
 *
 * | the reader | band | CTA |
 * |---|---|---|
 * | something landed in the window, wins not opened from the band yet | `ended-wins` | See your wins |
 * | opened the wins from the band, or nothing landed | `ended-plans` | See plans → the package picker |
 *
 * The wins band is a step, not an end: once its CTA is pressed the band turns into the plans band and
 * stays there until its X. The X on either over-band ends the campaign on this device.
 */
export type BlackFridayBannerState =
  | 'hidden'
  | 'offer'
  | 'running'
  | 'final'
  | 'ended-plans'
  | 'ended-wins';

export interface BlackFridayBannerInput {
  /** `specs.symphony.BlackFridayCampaign`. The whole feature is off without it. */
  campaignOn: boolean;
  /**
   * Whether the credits read has SETTLED. Before it does, "no open window" and "we have not looked
   * yet" are the same `null` — and offering 48 free hours to someone whose hours are already running
   * reads as broken. Unknown is not "no".
   */
  accountKnown: boolean;
  /** `freeUsageUntil` from the credits overview: the open window's end, or null when none is open. */
  freeUsageUntilIso: string | null;
  /** The last window end this device ever saw open. See the module note. */
  seenUntilIso: string | null;
  /** The offer band's X — persisted, so one close ends it. */
  offerDismissed: boolean;
  /** The over band's X — persisted, so one close ends it. */
  endedDismissed: boolean;
  /** Whether a win landed inside the window. Picks `ended-wins` over `ended-plans`; see the type. */
  hasWindowWins: boolean;
  /** The wins band's CTA was pressed — persisted. From then on the over-band is `ended-plans`. */
  winsOpened: boolean;
  now: number;
}

function activeUntil(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const until = new Date(iso).getTime();
  return Number.isFinite(until) && until > now ? until : null;
}

/**
 * Which band to draw. The one place the states are decided, shared by the banner, the modal gate
 * and the credits-card title.
 */
export function blackFridayBannerState(input: BlackFridayBannerInput): BlackFridayBannerState {
  const { campaignOn, accountKnown, freeUsageUntilIso, seenUntilIso, now } = input;
  if (!campaignOn || !accountKnown) return 'hidden';

  const open = activeUntil(freeUsageUntilIso, now);
  if (open !== null) return open - now <= BLACK_FRIDAY_FINAL_HOURS_MS ? 'final' : 'running';

  // No window open. Either this device watched one close, or it has never seen one at all.
  const ended = seenUntilIso ? new Date(seenUntilIso).getTime() : NaN;
  if (Number.isFinite(ended) && ended <= now) {
    if (input.endedDismissed) return 'hidden';
    return input.hasWindowWins && !input.winsOpened ? 'ended-wins' : 'ended-plans';
  }

  if (input.offerDismissed) return 'hidden';
  return 'offer';
}

/** Whether the band is one of the two counting down — the states whose copy changes every second. */
export function isBlackFridayCountdown(state: BlackFridayBannerState): boolean {
  return state === 'running' || state === 'final';
}

/**
 * `47:57:37` — the design's timer. Hours are NOT wrapped at 24 (the window is 48 long) and every field
 * is two digits, so the band's width does not jitter as the numbers fall. A window already closed reads
 * `00:00:00` rather than going negative.
 */
export function formatBlackFridayCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Whether any win landed inside the free window, which is what picks "See your wins" over "See plans".
 *
 * Anchored on the window rather than on a bare "last 48 hours" from now: the over-band can still be on
 * screen days later, and by then "the last 48 hours" is a different and empty stretch of time. The
 * window's start is derived from its end because the hours a code grants are what we know; several
 * grants add up, so this can under-reach on a stacked window, never over-reach.
 */
export function hasWinsInWindow(winCreatedDates: readonly string[], windowEndIso: string | null): boolean {
  if (!windowEndIso) return false;
  const end = new Date(windowEndIso).getTime();
  if (!Number.isFinite(end)) return false;
  const start = end - BLACK_FRIDAY_WINDOW_MS;
  return winCreatedDates.some((iso) => {
    const at = new Date(iso).getTime();
    return Number.isFinite(at) && at >= start && at <= end;
  });
}
